import * as THREE from "three";
import { PhysicsFriction, PhysicsMass, PhysicsRestitution } from "../physics/physics_masses.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import RAPIER from "@dimforge/rapier3d-compat";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Renderer } from "../core/renderer.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

export enum MetalBlockShape
{
  Square,
  Round,
  Iron,
}

export class MetalBlock extends BreakablePhysicsEntity implements LevelEntity
{
  private static readonly MODELS: Record<number, ResourceID> = {
    1: ResourceID.CannedBlockCube1x1Model,
    2: ResourceID.CannedBlockCube1x2Model,
    3: ResourceID.CannedBlockCube1x3Model,
  };

  private static readonly ROUND_MODELS: Record<number, ResourceID> = {
    1: ResourceID.CylinderColored1x1Model,
    2: ResourceID.CylinderColored1x2Model,
    3: ResourceID.CylinderColored1x3Model,
  };

  private static readonly IRON_MODELS: Record<number, ResourceID> = {
    1: ResourceID.CylinderGrey1x1Model,
    2: ResourceID.CylinderGrey1x2Model,
    3: ResourceID.CylinderGrey1x3Model,
  };

  private static readonly TEXTURES: readonly ResourceID[] = [
    ResourceID.SquareCanRedTexture,
    ResourceID.SquareCanOrangeTexture,
    ResourceID.SquareCanYellowTexture,
    ResourceID.SquareCanGreenTexture,
    ResourceID.SquareCanLightBlueTexture,
    ResourceID.SquareCanBlueTexture,
    ResourceID.SquareCanPinkTexture,
  ];

  private static readonly ROUND_TEXTURES: readonly ResourceID[] = [
    ResourceID.CylinderRedTexture,
    ResourceID.CylinderOrangeTexture,
    ResourceID.CylinderYellowTexture,
    ResourceID.CylinderGreenTexture,
    ResourceID.CylinderLightBlueTexture,
    ResourceID.CylinderBlueTexture,
    ResourceID.CylinderPinkTexture,
  ];

  private static readonly IRON_TEXTURES: readonly ResourceID[] = [
    ResourceID.IronCanAltTexture,
  ];

  private static readonly COLOR_ORANGE = 1;
  private static readonly COLOR_YELLOW = 2;
  private static readonly COLOR_GREEN = 3;

  private static readonly EMISSIVE_DEFAULT = 0.35;
  private static readonly EMISSIVE_IRON = 0.08;
  private static readonly EMISSIVE_YELLOW = 0.12;
  private static readonly EMISSIVE_GREEN = 0.2;

  private static readonly SPECULAR_DEFAULT = 0xbbbbbb;
  private static readonly SPECULAR_IRON = 0x777777;
  private static readonly SPECULAR_YELLOW = 0x1c1b03;
  private static readonly SPECULAR_ORANGE = 0x584913;

  private static readonly materials = new Map<string, THREE.MeshPhongMaterial>();
  private static environment_map: THREE.Texture | null = null;
  private static readonly FADE_DURATION = 0.35;
  private static readonly OUTSIDE_TIMER_DURATION = 4;

  public readonly kind = EntityKind.Metal;

  // A block occupies exactly one of these two axes (1-3), never both — the
  // model only exists as a vertical column, so a "wide" piece is the same
  // model/texture rotated -90° onto its side (see prepareVisual). Matches
  // PhysicsCube.createAuthoredMesh's `width > height` rotation convention.
  public readonly width_blocks: number;
  public readonly height_blocks: number;
  public readonly variant_index: number;
  public readonly shape: MetalBlockShape;

  private outside_pending = false;
  private outside_timer = 0;
  private fade_timer = 0;

  constructor(
    position: THREE.Vector3,
    width_blocks = 1,
    height_blocks = 1,
    variant_index?: number,
    shape = MetalBlockShape.Square,
    lying = false,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const blocks = THREE.MathUtils.clamp( Math.round( Math.max( width_blocks, height_blocks ) ), 1, 3 );
    const horizontal = width_blocks > height_blocks;
    const texture_count = shape === MetalBlockShape.Iron
      ? MetalBlock.IRON_TEXTURES.length
      : shape === MetalBlockShape.Round
        ? MetalBlock.ROUND_TEXTURES.length
        : MetalBlock.TEXTURES.length;
    const safe_variant = variant_index === undefined
      ? Math.floor( Math.random() * texture_count )
      : ( ( variant_index % texture_count ) + texture_count ) % texture_count;

    const mesh = MetalBlock.createMesh( blocks, horizontal, safe_variant, shape, lying );

    super( mesh, position, {
      angular_damping: 0.4,
      arm_delay: 0,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
      friction: PhysicsFriction.metal_block,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      linear_damping: 0.05,
      mass: PhysicsMass.metal_block_per_cell * blocks,
      restitution: PhysicsRestitution.metal_block,
    } );

    this.width_blocks = horizontal ? blocks : 1;
    this.height_blocks = horizontal ? 1 : blocks;
    this.variant_index = safe_variant;
    this.shape = shape;
  }

  private static createMesh( blocks: number, horizontal: boolean, variant_index: number, shape: MetalBlockShape, lying: boolean ): THREE.Object3D
  {
    const model_id = shape === MetalBlockShape.Round
      ? MetalBlock.ROUND_MODELS[blocks]
      : shape === MetalBlockShape.Iron
        ? MetalBlock.IRON_MODELS[blocks]
        : MetalBlock.MODELS[blocks];
    const visual = MetalBlock.prepareVisual( model_id, blocks, horizontal, variant_index, shape );
    const group = new THREE.Group();
    group.add( visual );

    if ( lying ) group.rotation.x = Math.PI / 2;

    const root = new THREE.Group();
    root.add( group );
    return root;
  }

  private static prepareVisual( model_id: ResourceID, blocks: number, horizontal: boolean, variant_index: number, shape: MetalBlockShape ): THREE.Object3D
  {
    const visual = ResourceManager.getModel( model_id );
    const body_material = MetalBlock.getMaterial( variant_index, shape );

    visual.updateMatrixWorld( true );

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      child.castShadow = true;
      child.receiveShadow = false;
      child.material = body_material;
    } );

    // The model is authored standing up along local Y — lay it on its side
    // for a horizontal (width > height) piece, same convention as
    // PhysicsCube.createAuthoredMesh's authored long models.
    if ( horizontal )
      visual.rotation.z = -Math.PI * 0.5;

    visual.updateMatrixWorld( true );
    const base_size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    const target_size = PhysicsCube.getSize();

    if ( horizontal )
    {
      const radial_scale = Math.min( target_size.y, target_size.z ) / Math.max( base_size.y, base_size.z );
      visual.scale.set( target_size.x * blocks / base_size.x, radial_scale, radial_scale );
    }
    else
    {
      const radial_scale = Math.min( target_size.x, target_size.z ) / Math.max( base_size.x, base_size.z );
      visual.scale.set( radial_scale, target_size.y * blocks / base_size.y, radial_scale );
    }

    visual.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( visual ).getCenter( new THREE.Vector3() );
    visual.position.sub( center );

    return visual;
  }

  private static getMaterial( variant_index: number, shape: MetalBlockShape ): THREE.MeshPhongMaterial
  {
    const material_key = `${shape}:${variant_index}`;
    const cached = this.materials.get( material_key );
    if ( cached ) return cached;

    const texture_id = shape === MetalBlockShape.Round
      ? this.ROUND_TEXTURES[variant_index]
      : shape === MetalBlockShape.Iron
        ? this.IRON_TEXTURES[variant_index]
        : this.TEXTURES[variant_index];
    const normal_id = shape === MetalBlockShape.Round
      ? null
      : shape === MetalBlockShape.Iron
        ? ResourceID.IronCanNormal
        : ResourceID.MetalNormalMap;
    const source_map = ResourceManager.getTexture( texture_id );
    const normal_map = normal_id === null ? null : ResourceManager.getNormalMap( normal_id );
    // These textures already contain authored highlights and colour grading.
    // A neutral material preserves the PNG appearance under the scene lights
    // without applying a second layer of environment reflections or clearcoat.
    const is_iron = shape === MetalBlockShape.Iron;

    const emissive_intensity = is_iron
      ? MetalBlock.EMISSIVE_IRON
      : variant_index === MetalBlock.COLOR_YELLOW
        ? MetalBlock.EMISSIVE_YELLOW
        : variant_index === MetalBlock.COLOR_GREEN
          ? MetalBlock.EMISSIVE_GREEN
          : MetalBlock.EMISSIVE_DEFAULT;

    const specular_hex = is_iron
      ? MetalBlock.SPECULAR_IRON
      : variant_index === MetalBlock.COLOR_YELLOW
        ? MetalBlock.SPECULAR_YELLOW
        : variant_index === MetalBlock.COLOR_ORANGE
          ? MetalBlock.SPECULAR_ORANGE
          : MetalBlock.SPECULAR_DEFAULT;

    const material = new THREE.MeshPhongMaterial( {
      map: source_map,
      normalMap: normal_map,
      normalScale: new THREE.Vector2( 0.4, 0.4 ),
      color: is_iron ? 0xe0e0e0 : 0xffffff,
      shininess: is_iron ? 30 : 80,
      specular: new THREE.Color( specular_hex ),
      envMap: is_iron ? this.getEnvironmentMap() : null,
      reflectivity: is_iron ? 0.25 : 0,
      combine: THREE.MixOperation,

      // At high metalness the diffuse texture contribution becomes weak.
      // Feed part of the authored map back as emissive so every can shape
      // keeps its intended brightness while retaining metal reflections.
      emissive: new THREE.Color( 0xffffff ),
      emissiveMap: source_map,
      emissiveIntensity: emissive_intensity,
    } );
    this.materials.set( material_key, material );
    return material;
  }

  private static getEnvironmentMap(): THREE.Texture
  {
    if ( this.environment_map )
      return this.environment_map;

    const generator = new THREE.PMREMGenerator( Renderer.renderer );
    const environment = new RoomEnvironment();
    this.environment_map = generator.fromScene( environment, 0.04 ).texture;
    environment.dispose();
    generator.dispose();
    return this.environment_map;
  }

  // A block covers a few hundred screen pixels at most, so the authored 2048²
  // source is resampled down before any per-pixel work: it makes the CPU pass
  // 16x cheaper and keeps the GPU from streaming 4M-pixel mip chains.
  private static readonly MAX_TEXTURE_SIZE = 512;

  private static targetSize( image: { width: number; height: number } ): { w: number; h: number }
  {
    const scale = Math.min( 1, this.MAX_TEXTURE_SIZE / Math.max( image.width, image.height ) );
    return {
      w: Math.max( 1, Math.round( image.width * scale ) ),
      h: Math.max( 1, Math.round( image.height * scale ) ),
    };
  }

  private static createGradedBaseColorTexture( source: THREE.Texture ): THREE.CanvasTexture
  {
    const image = source.image as CanvasImageSource & { width: number; height: number };
    const size = MetalBlock.targetSize( image );
    const canvas = document.createElement( "canvas" );
    canvas.width = size.w;
    canvas.height = size.h;

    const ctx = canvas.getContext( "2d", { willReadFrequently: true } )!;
    ctx.drawImage( image, 0, 0, canvas.width, canvas.height );

    const pixels = ctx.getImageData( 0, 0, canvas.width, canvas.height );
    const data = pixels.data;

    for ( let offset = 0; offset < data.length; offset += 4 )
    {
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      const is_pale_body = b > 140 && g > 140 && Math.abs( g - b ) < 80 && r < g;
      const factor = is_pale_body ? 0.92 : 1;

      data[offset] = Math.round( r * factor );
      data[offset + 1] = Math.round( g * factor );
      data[offset + 2] = Math.round( b * factor );
    }

    ctx.putImageData( pixels, 0, 0 );

    const texture = new THREE.CanvasTexture( canvas );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.flipY = source.flipY;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = 4;
    texture.needsUpdate = true;
    return texture;
  }

  private static createMetalnessTexture( source: THREE.Texture ): THREE.CanvasTexture
  {
    const image = source.image as CanvasImageSource & { width: number; height: number };
    const size = MetalBlock.targetSize( image );
    const canvas = document.createElement( "canvas" );
    canvas.width = size.w;
    canvas.height = size.h;

    const ctx = canvas.getContext( "2d", { willReadFrequently: true } )!;
    ctx.drawImage( image, 0, 0, canvas.width, canvas.height );

    const pixels = ctx.getImageData( 0, 0, canvas.width, canvas.height );
    const data = pixels.data;

    for ( let offset = 0; offset < data.length; offset += 4 )
    {
      const r = data[offset];
      const g = data[offset + 1];
      const b = data[offset + 2];
      // The authored gold trim is warm yellow/orange. Keep the pale-blue
      // body, red target and blue gems dielectric.
      const is_gold = r > 140 && g > 65 && b < 105 && r > g * 1.08;
      const metalness = is_gold ? 255 : 0;
      data[offset] = metalness;
      data[offset + 1] = metalness;
      data[offset + 2] = metalness;
      data[offset + 3] = 255;
    }

    ctx.putImageData( pixels, 0, 0 );

    const texture = new THREE.CanvasTexture( canvas );
    texture.colorSpace = THREE.NoColorSpace;
    texture.flipY = source.flipY;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = 4;
    texture.needsUpdate = true;
    return texture;
  }

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    if ( this.destroying ) return;
    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();

    if ( !is_outside )
    {
      this.outside_pending = false;
      return;
    }

    // Destroy only when hitting the ground plane (bottom_y <= 0.2) or falling deep below floor level
    if ( bottom_y <= 0.2 || pos.y < -2.0 )
    {
      this.destroy();
      return;
    }

    if ( !this.outside_pending && table.isBelowSurface( pos ) )
    {
      this.outside_pending = true;
      this.outside_timer = MetalBlock.OUTSIDE_TIMER_DURATION;
    }

    // Unconditional after the timer: a block parked above the ground (on the
    // table base or wedged on the leg) would otherwise stay outside_pending
    // forever, and isAtRest() === false would block the game-end check.
    if ( this.outside_pending && ( this.outside_timer -= dt ) <= 0 )
    {
      this.destroy();
    }
  }

  public isAtRest(): boolean
  {
    return !this.outside_pending && super.isAtRest();
  }

  public playHitSound(): void
  {
    const clip = this.shape === MetalBlockShape.Round
      ? AudioClips.METAL_ROUND_HIT
      : this.shape === MetalBlockShape.Iron
        ? ( Math.random() < 0.5 ? AudioClips.TIN_CAN_HIT_1 : AudioClips.TIN_CAN_HIT_2 )
        : AudioClips.METAL_CUBE_HIT;

    void audioController.playSound( clip );
  }

  public canBeHit(): boolean
  {
    return this.isAlive();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    this.playHitSound();
    Vfx3DManager.addCannonBallBlockImpact( point, BlockImpactMaterial.METAL );
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    this.destroy();
  }

  public checkSelfDestruct( dt: number, table: PhysicsTable ): void
  {
    this.checkDestroy( dt, table );   // те, що вже є
  }

  private setOpacity( opacity: number ): void
  {
    this.mesh.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      if ( !child.userData.materialCloned )
      {
        child.material = Array.isArray( child.material )
          ? child.material.map( material => material.clone() )
          : child.material.clone();
        child.userData.materialCloned = true;
      }
      const materials = Array.isArray( child.material ) ? child.material : [ child.material ];
      for ( const material of materials )
      {
        material.transparent = true;
        material.opacity = opacity;
        material.depthWrite = false;
      }
      child.castShadow = false;
    } );
  }

  protected updateDestroying( dt: number ): void
  {
    this.fade_timer -= dt;
    this.setOpacity( Math.max( 0, this.fade_timer / MetalBlock.FADE_DURATION ) );
    if ( this.fade_timer <= 0 ) this.markDead();
  }

  public destroy(): void
  {
    if ( !this.beginDestroying() ) return;
    this.fade_timer = MetalBlock.FADE_DURATION;
  }

}
